import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { DayLog, Note, Project, Task } from "./api";

export type Entity = "task" | "project" | "dayLog" | "note";

export interface OutboxOp {
  opId?: number; // autoincrement key
  entity: Entity;
  type: "upsert" | "delete";
  id: string;
  ts: number; // epoch ms (updatedAt for upsert, deletedAt for delete)
  data?: Record<string, unknown>;
}

interface FDSchema extends DBSchema {
  tasks: { key: string; value: Task };
  projects: { key: string; value: Project };
  dayLogs: { key: string; value: DayLog };
  notes: { key: string; value: Note };
  outbox: { key: number; value: OutboxOp };
  meta: { key: string; value: { key: string; value: string } };
}

let dbp: Promise<IDBPDatabase<FDSchema>> | null = null;

function db() {
  if (!dbp) {
    // v2 adds the `notes` store. `upgrade` runs for both fresh DBs and v1→v2
    // upgrades, so each createObjectStore is guarded to stay idempotent.
    dbp = openDB<FDSchema>("founderdeck", 2, {
      upgrade(d) {
        const ensure = (name: "tasks" | "projects" | "dayLogs" | "notes" | "outbox" | "meta") => {
          if (d.objectStoreNames.contains(name)) return false;
          return true;
        };
        if (ensure("tasks")) d.createObjectStore("tasks", { keyPath: "id" });
        if (ensure("projects")) d.createObjectStore("projects", { keyPath: "id" });
        if (ensure("dayLogs")) d.createObjectStore("dayLogs", { keyPath: "id" });
        if (ensure("notes")) d.createObjectStore("notes", { keyPath: "id" });
        if (ensure("outbox")) d.createObjectStore("outbox", { keyPath: "opId", autoIncrement: true });
        if (ensure("meta")) d.createObjectStore("meta", { keyPath: "key" });
      },
    });
  }
  return dbp;
}

const STORE = { task: "tasks", project: "projects", dayLog: "dayLogs", note: "notes" } as const;

export async function getAll<T>(
  store: "tasks" | "projects" | "dayLogs" | "notes",
): Promise<T[]> {
  return (await db()).getAll(store) as Promise<T[]>;
}

export async function putRecord(entity: Entity, value: unknown): Promise<void> {
  await (await db()).put(STORE[entity], value as never);
}

export async function deleteRecord(entity: Entity, id: string): Promise<void> {
  await (await db()).delete(STORE[entity], id);
}

export async function getRecord<T>(entity: Entity, id: string): Promise<T | undefined> {
  return (await db()).get(STORE[entity], id) as Promise<T | undefined>;
}

// ---- outbox ----
export async function enqueue(op: OutboxOp): Promise<void> {
  await (await db()).add("outbox", op);
}
export async function outboxAll(): Promise<OutboxOp[]> {
  return (await db()).getAll("outbox");
}
export async function outboxRemove(ids: number[]): Promise<void> {
  const tx = (await db()).transaction("outbox", "readwrite");
  for (const id of ids) await tx.store.delete(id);
  await tx.done;
}

// ---- meta ----
export async function metaGet(key: string): Promise<string | undefined> {
  const row = await (await db()).get("meta", key);
  return row?.value;
}
export async function metaSet(key: string, value: string): Promise<void> {
  await (await db()).put("meta", { key, value });
}
